import { Category, ConnectorStatus, CredentialStatus, MfaStatus, UserCoverageSummary } from '@ampsec/platform-client';
import classNames from 'classnames';
import { CustomTooltip } from 'Components/atoms';
import IconWithStatus from 'Components/molecules/IconWithStatus/IconWithStatus';
import { ToolingCoverageTooltip } from 'Components/molecules/ToolingCoverageTooltip';
import {
  COLLABORATION,
  IDENTITY_AND_ACCESS,
} from 'Core-utils/constants/constants';
import {
  DateTimeFormatter,
  getAssetDomains,
  getMFAStatusProps,
  getMFATooltipProps,
} from 'Core-utils/helpers/helpers';
import _ from 'lodash';
import React, { ComponentProps } from 'react';
import {
  formatCsvTooltipGroups,
  type CsvTooltipField,
  type CsvTooltipGroup,
} from '../../AgGridBase/exportCsv';
import { AssetProviderType, AssetsType, GridContext } from '../AgGridCompliance.config';
import { getUserConnectorStatus } from '../ToolingComplianceComponentHelper';
import { FaQuestionCircle } from '@react-icons/all-files/fa/FaQuestionCircle';
import { FaExclamationTriangle } from '@react-icons/all-files/fa/FaExclamationTriangle';

const ALLOWED_COLLABORATION_PROVIDERS = ['slack', 'teams'];

function normalize(value: string): string {
  return _.startCase((value ?? '').toLowerCase().replace(/_/g, ' '));
}

// Modifier class (e.g. 'provider-logo--crowdstrike') derived from the provider name, so a
// single provider's logo can be re-styled without touching the other coverage-grid logos.
function getProviderLogoModifier(name?: string): string | undefined {
  const slug = (name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return slug ? `provider-logo--${slug}` : undefined;
}

export function getLastSyncedText(lastRunTime: string | undefined): string {
  if (!lastRunTime) {
    return 'NEVER';
  }
  return DateTimeFormatter(new Date(lastRunTime).toISOString());
}

/**
 * The providers (installed connectors matching the column's domain) whose icons a domain cell
 * renders — per user account for the user domains, per asset for the asset domains. Shared by the
 * cell renderer and the CSV export so both describe the same set of providers.
 */
export function resolveProviders(
  row: UserCoverageSummary,
  asset: AssetsType | undefined,
  domain: string,
  isAssetCell: boolean,
  context: GridContext,
): AssetProviderType[] {
  const { tenantSettings, providerNameIntegrationMap, isLoading, allConnectors } = context;

  const rawConnectors = isAssetCell && asset
    ? Object.keys(asset.assetAccounts2).map((cid) => ({
        ...allConnectors[cid],
        count: asset.assetAccounts2[cid].length,
        type: 'asset' as const,
      }))
    : Object.keys(row.userAccounts2).map((cid) => ({
        ...allConnectors[cid],
        count: row.userAccounts2[cid].length,
        type: 'user' as const,
      }));

  const providers = rawConnectors
    .map((connector) => {
      const contentfulData = providerNameIntegrationMap?.[connector.provider?.displayValue];
      const assetDomains = tenantSettings
        ? getAssetDomains(connector.id, connector.provider?.displayValue, tenantSettings)
        : [];
      const domains =
        !isLoading && _.isEmpty(assetDomains)
          ? contentfulData?.domains?.map((d: any) => d.fields.name)
          : assetDomains;

      return {
        count: connector.count,
        type: connector.type,
        name: contentfulData?.name,
        domains,
        logo: contentfulData?.logo?.fields?.file?.url,
        status: connector.status,
        credentialStatus: connector.credentialStatus,
        lastRunTime: connector.lastRunTime,
        id: connector.id,
      } as AssetProviderType;
    })
    .filter((p) => p.domains?.includes(domain));

  if (domain === COLLABORATION) {
    return providers.filter((p) =>
      ALLOWED_COLLABORATION_PROVIDERS.some((name) => new RegExp(name, 'i').test(p.name)),
    );
  }

  return providers;
}

interface MissingConnectorIndicatorProps {
  domain: string;
  context: GridContext;
}

/**
 * Whether a domain cell shows the "no connector installed / disabled" state or the "account or tool
 * missing" state. Shared by the indicator and the CSV export so both report the same reason.
 */
export function getMissingConnectorText(domain: string, context: GridContext): string {
  const { domainsWithNoConnectors, extractedData } = context.connectorDataHook;
  const filteredCategoryConnectors = extractedData?.filter((data: any) =>
    data.category.map((cat: any) => normalize(cat)).includes(domain as Category),
  );
  const isDisabledConnector =
    !_.isEmpty(filteredCategoryConnectors) &&
    filteredCategoryConnectors.every((item: any) => item.status === ConnectorStatus.DISABLED);

  const noMatchingConnector = domainsWithNoConnectors?.includes(domain) || isDisabledConnector;
  return noMatchingConnector ? 'Disabled connector' : 'Account/tool missing';
}

const MissingConnectorIndicator = ({ domain, context }: MissingConnectorIndicatorProps) => {
  const noMatchingConnector = getMissingConnectorText(domain, context) === 'Disabled connector';

  const icon = noMatchingConnector ? (
    <FaQuestionCircle className="text-text-low-emphasis mt-2" size={20} style={{ opacity: 0.3 }} />
  ) : (
    <FaExclamationTriangle className="text-text-low-emphasis mt-2" size={20} style={{ opacity: 0.5 }} />
  );

  return (
    <div data-testid="missing-connector-indicator">
      <CustomTooltip label={noMatchingConnector ? 'Disabled connector' : 'Account/tool missing'}>{icon}</CustomTooltip>
    </div>
  );
};

/**
 * One row of a provider account tooltip, exactly as the tooltip renders it. Rows with `isMfaFactor`
 * render as `ToolingCoverageTooltip.CustomItem`s (icon + value) under the MFA Factors heading; the
 * CSV export joins those values into a single 'MFA Factors' field instead.
 */
export type DomainTooltipRow = {
  label: string;
  value: string;
  status?: 'warn' | 'error';
  isMfaFactor?: boolean;
  icon?: React.ReactNode;
};

interface AccountSummary {
  active?: boolean;
  lastActivity?: string;
}

function getLastActivityLabel(domain: string): string {
  return domain === IDENTITY_AND_ACCESS ? 'Last Logged in' : 'Last Check in';
}

function getAccountStatus(domain: string, provider: AssetProviderType, account: AccountSummary | undefined) {
  const isActive = account?.active ?? false;
  return getUserConnectorStatus(isActive, (account as any)?.lastActivity, {
    considersLastActivity: (domain !== COLLABORATION && provider.name?.toLowerCase() !== 'amplifier'),
  });
}

/**
 * The tooltip rows of one provider account (connector status, synced at, last logged in / check in,
 * tool status, secure status and MFA factors). Both the cell renderer and the CSV export read from
 * here so the exported detail cannot drift from what the UI renders.
 */
export function getProviderTooltipRows(
  row: UserCoverageSummary,
  provider: AssetProviderType,
  account: AccountSummary | undefined,
  domain: string,
): DomainTooltipRow[] {
  const hasMfaData = domain === COLLABORATION || domain === IDENTITY_AND_ACCESS;
  const mfaStatus = hasMfaData
    ? (row.mfaReports?.[provider.id]?.mfaStatus ?? MfaStatus.DISABLED)
    : undefined;
  const mfaFactors = hasMfaData ? (row.mfaReports?.[provider.id]?.mfaFactors ?? undefined) : undefined;
  const isActive = account?.active ?? false;
  const lastActivity = (account as any)?.lastActivity as string | undefined;
  const computedStatus = getAccountStatus(domain, provider, account);
  const showsLastActivity = domain !== COLLABORATION && provider.name?.toLowerCase() !== 'amplifier';

  const rows: DomainTooltipRow[] = [
    {
      label: 'Connector Status',
      value: `${provider.status}`,
      status: provider.status !== ConnectorStatus.ACTIVE && provider.credentialStatus !== CredentialStatus.VALID ? 'warn' : undefined,
    },
    {
      label: 'Connector Synced At',
      value: getLastSyncedText(provider.lastRunTime),
      status: provider.lastRunTime ? undefined : 'warn',
    },
  ];

  if (showsLastActivity) {
    rows.push({
      label: getLastActivityLabel(domain),
      value: lastActivity ? DateTimeFormatter(lastActivity) : 'NEVER',
      status: computedStatus !== 'active' ? computedStatus : undefined,
    });
  }

  rows.push({
    label: 'Tool Status',
    value: isActive ? 'ACTIVE' : 'DISABLED',
    status: !isActive ? 'warn' : undefined,
  });

  if (domain === IDENTITY_AND_ACCESS) {
    rows.push({ label: 'Secure', value: mfaStatus?.split('_').join(' ') ?? 'DISABLED' });

    getMFATooltipProps(mfaFactors).forEach((item, idx) => {
      rows.push({
        label: idx === 0 ? 'MFA Factors' : ' ',
        value: item.value,
        isMfaFactor: true,
        icon: item.icon,
      });
    });
  }

  return rows;
}

/** One provider account tooltip group per CSV line-item, MFA factors folded into one field. */
function toDomainTooltipFields(rows: DomainTooltipRow[]): CsvTooltipField[] {
  const fields = rows
    .filter((row) => !row.isMfaFactor)
    .map(({ label, value }) => ({ label, value }));
  const factors = rows.filter((row) => row.isMfaFactor).map((row) => row.value);
  if (factors.length > 0) {
    fields.push({ label: 'MFA Factors', value: factors.join(', ') });
  }
  return fields;
}

function sortProvidersByName(providers: AssetProviderType[]): AssetProviderType[] {
  return providers.slice().sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);
}

/**
 * The CSV tooltip cell of a domain column: one group per provider account icon the cell renders
 * (provider name followed by its tooltip rows), in the same order the icons appear. A cell that
 * renders the missing-connector indicator instead contributes that state ('Disabled connector' /
 * 'Account/tool missing'), so the export explains an empty cell the same way the UI does. Asset
 * domain cells group per asset (`<asset> - <provider>`), matching the one-tooltip-per-asset rows.
 */
export function getDomainTooltipExport(
  row: UserCoverageSummary,
  domain: string,
  isAssetCell: boolean,
  context: GridContext,
): string {
  const groups: CsvTooltipGroup[] = [];

  const appendAsset = (asset: AssetsType | undefined): void => {
    const providers = resolveProviders(row, asset, domain, isAssetCell, context);
    const totalCount = providers.reduce((sum, provider) => sum + provider.count, 0);

    if (providers.length === 0 || totalCount === 0) {
      groups.push({ label: getMissingConnectorText(domain, context), fields: [] });
      return;
    }

    const accounts = isAssetCell ? asset?.assetAccounts2 : row.userAccounts2;
    sortProvidersByName(providers).forEach((provider) => {
      new Array(provider.count).fill(0).forEach((_, index) => {
        const account = accounts?.[provider.id]?.[index];
        groups.push({
          label: isAssetCell ? `${asset?.displayValue} - ${provider.name}` : provider.name,
          fields: toDomainTooltipFields(getProviderTooltipRows(row, provider, account, domain)),
        });
      });
    });
  };

  if (isAssetCell) {
    (row.assets ?? []).forEach((asset) => appendAsset(asset));
    return formatCsvTooltipGroups(groups);
  }

  // The unassigned row renders no icon and no indicator, so it has no tooltip to export either.
  if (row.name?.toLowerCase() === 'unassigned') {
    return '';
  }
  appendAsset(row.assets?.[0]);
  return formatCsvTooltipGroups(groups);
}

interface ProviderIconsProps {
  row: UserCoverageSummary;
  asset: AssetsType;
  provider: AssetProviderType;
  domain: string;
  isAssetCell: boolean;
}

const ProviderIcons = ({ row, asset, provider, domain, isAssetCell }: ProviderIconsProps) => {
  const accounts = isAssetCell ? asset?.assetAccounts2 : row.userAccounts2;
  const mfaStatus = (domain === COLLABORATION || domain === IDENTITY_AND_ACCESS)
    ? (row.mfaReports?.[provider.id]?.mfaStatus ?? MfaStatus.DISABLED)
    : undefined;
  return (
    <>
      {new Array(provider.count).fill(0).map((_, index) => {
        const account = accounts?.[provider.id]?.[index];
        const isActive = account?.active ?? false;
        const computedStatus = getAccountStatus(domain, provider, account);

        type SecondaryStatus = Pick<
          ComponentProps<typeof IconWithStatus>,
          'secondaryStatus'
        >['secondaryStatus'];
        const secondaryStatus: SecondaryStatus =
          mfaStatus && domain === IDENTITY_AND_ACCESS && mfaStatus !== MfaStatus.NOT_ENROLLED
            ? (getMFAStatusProps(mfaStatus).variantValue as SecondaryStatus)
            : undefined;

        const tooltipRows = getProviderTooltipRows(row, provider, account, domain);
        const logoModifier = getProviderLogoModifier(provider.name);
        const logoClassName = classNames('users-insights-modal__provider_logo', logoModifier);

        return (
          <div
            key={`${provider.domains?.join('-')}-${provider.name}-${index}`}
            className="users-insights-modal__cont"
            data-testid={provider.id}
          >
            <ToolingCoverageTooltip>
              <ToolingCoverageTooltip.Trigger>
                <IconWithStatus
                  imageSrc={provider.logo}
                  status={computedStatus}
                  iconClassName={logoClassName}
                  secondaryStatus={secondaryStatus}
                />
              </ToolingCoverageTooltip.Trigger>

              <ToolingCoverageTooltip.Header
                icon={<IconWithStatus imageSrc={provider.logo} status={isActive ? 'active' : 'warn'} iconClassName={logoModifier} />}
                label={provider.name}
              />
              {tooltipRows.map((tooltipRow, idx) => (
                tooltipRow.isMfaFactor ? (
                  <ToolingCoverageTooltip.CustomItem
                    label={tooltipRow.label}
                    key={`mfa-${idx}`}
                    icon={tooltipRow.icon}
                    value={tooltipRow.value}
                  />
                ) : (
                  <ToolingCoverageTooltip.Row
                    key={`${tooltipRow.label}-${idx}`}
                    label={tooltipRow.label}
                    value={tooltipRow.value}
                    status={tooltipRow.status}
                  />
                )
              ))}
            </ToolingCoverageTooltip>
          </div>
        );
      })}
    </>
  );
};

interface ProviderIconRowProps {
  row: UserCoverageSummary;
  asset: AssetsType;
  providers: AssetProviderType[];
  domain: string;
  isAssetCell: boolean;
}

const ProviderIconRow = ({ row, asset, providers, domain, isAssetCell }: ProviderIconRowProps) => (
  <div className="coverage-cell__provider-row">
    {providers
      .slice()
      .sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0)
      .map((provider) => (
        <React.Fragment key={`${provider.domains?.join('-')}-${provider.id}`}>
          <ProviderIcons
            row={row}
            asset={asset}
            provider={provider}
            domain={domain}
            isAssetCell={isAssetCell}
          />
        </React.Fragment>
      ))}
  </div>
);

interface DomainCellRendererProps {
  data: UserCoverageSummary;
  context: GridContext;
  domain: string;
  isAssetCell: boolean;
}

const DomainCellRenderer = ({ data: row, context, domain, isAssetCell }: DomainCellRendererProps) => {
  const firstAsset = row.assets[0];

  if (isAssetCell) {
    return (
      <div
        className={classNames('coverage-cell coverage-cell--column', {
          'coverage-cell--transparent': !firstAsset?.isAssetActive,
        })}
      >
        {row.assets.map((asset) => {
          const providers = resolveProviders(row, asset, domain, true, context);
          const totalCount = providers.reduce((sum, p) => sum + p.count, 0);

          if (providers.length === 0 || totalCount === 0) {
            return (
              <div key={asset.id}>
                <MissingConnectorIndicator domain={domain} context={context} />
              </div>
            );
          }

          return (
            <div key={asset.id} className="users-insights-modal__cont mb-3">
              <ProviderIconRow
                row={row}
                asset={asset}
                providers={providers}
                domain={domain}
                isAssetCell={true}
              />
            </div>
          );
        })}
      </div>
    );
  }

  const providers = resolveProviders(row, firstAsset, domain, false, context);
  const totalCount = providers.reduce((sum, p) => sum + p.count, 0);

  if (providers.length === 0 || totalCount === 0) {
    if (row.name?.toLowerCase() === 'unassigned') {
      return null;
    }
    return <MissingConnectorIndicator domain={domain} context={context} />;
  }

  return (
    <ProviderIconRow
      row={row}
      asset={firstAsset}
      providers={providers}
      domain={domain}
      isAssetCell={false}
    />
  );
};

export default DomainCellRenderer;
